/**
 * /privacy — OneStamp Privacy Policy (public, no auth).
 *
 * Required for Apple App Store resubmission. Public route, reachable
 * from any hostname (apex, my.*, staff.*). Mobile-responsive. Sticky
 * in-page TOC at lg+ breakpoint; collapses to top-of-page on small
 * screens.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUp, ChevronLeft } from 'lucide-react';

const LAST_UPDATED = '2026-06-05';

const SECTIONS = [
  { id: 'introduction', label: '1. Introduction' },
  { id: 'information-we-collect', label: '2. Information We Collect' },
  { id: 'how-we-use-information', label: '3. How We Use Information' },
  { id: 'loyalty-program-data', label: '4. Loyalty Program Data' },
  { id: 'rewards-and-redemption-data', label: '5. Rewards & Redemption Data' },
  { id: 'cookies-and-analytics', label: '6. Cookies & Analytics' },
  { id: 'data-sharing', label: '7. Data Sharing' },
  { id: 'data-retention', label: '8. Data Retention' },
  { id: 'account-deletion', label: '9. Account Deletion' },
  { id: 'security', label: '10. Security' },
  { id: 'user-rights', label: '11. Your Rights' },
  { id: 'contact', label: '12. Contact Information' },
];

export default function PrivacyPolicyPage() {
  useEffect(() => {
    const prev = document.title;
    document.title = 'OneStamp Privacy Policy';
    return () => { document.title = prev; };
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-zinc-200" data-testid="privacy-policy-page">
      {/* Page chrome — back link + top brand */}
      <header className="border-b border-white/5 bg-[#0A0A0A]/95 backdrop-blur supports-[backdrop-filter]:bg-[#0A0A0A]/70 sticky top-0 z-10 print:hidden">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3">
          <Link
            to="/"
            data-testid="privacy-back"
            className="inline-flex items-center gap-1 text-zinc-400 hover:text-[#C9A84C] transition text-sm"
            aria-label="Back to OneStamp"
          >
            <ChevronLeft size={16} />
            <span>OneStamp</span>
          </Link>
        </div>
      </header>

      {/* Body: TOC sidebar (lg+) + article */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14 lg:py-16 grid grid-cols-1 lg:grid-cols-[220px,1fr] gap-10">

        {/* TOC — sticky on lg, in-flow on mobile */}
        <nav
          aria-label="Privacy policy table of contents"
          className="lg:sticky lg:top-20 lg:self-start print:hidden"
          data-testid="privacy-toc"
        >
          <h2 className="text-xs uppercase tracking-[0.18em] text-zinc-500 mb-3">
            On this page
          </h2>
          <ol className="space-y-1.5 text-sm">
            {SECTIONS.map((s) => (
              <li key={s.id}>
                <a
                  href={`#${s.id}`}
                  className="block py-1 text-zinc-400 hover:text-[#C9A84C] transition"
                >
                  {s.label}
                </a>
              </li>
            ))}
          </ol>
        </nav>

        {/* Article */}
        <article className="max-w-2xl">
          <p className="text-xs uppercase tracking-[0.18em] text-zinc-500">
            Last updated: {LAST_UPDATED}
          </p>
          <h1 className="mt-2 text-3xl sm:text-4xl font-semibold tracking-tight text-white">
            Privacy Policy
          </h1>
          <p className="mt-4 text-zinc-400">
            OneStamp is a multi-brand digital loyalty platform. This
            Privacy Policy describes how we collect, use, store, and
            protect personal data when you use the OneStamp mobile app
            and Customer Web.
          </p>

          {/* §1 */}
          <Section id="introduction" title="1. Introduction">
            <p>
              This policy is published by OneStamp (referred to as
              &quot;OneStamp&quot;, &quot;we&quot;, &quot;us&quot;, or
              &quot;our&quot;). It describes how personal data is collected, used,
              and protected when you access the OneStamp service.
            </p>
            <p>
              <strong>Effective date:</strong> {LAST_UPDATED}.
            </p>
            <p>
              OneStamp operates in Indonesia and complies with{' '}
              <em>Undang-Undang Perlindungan Data Pribadi</em> (UU PDP, Law No. 27/2022 on Personal
              Data Protection). Where this policy refers to a specific Indonesian statute, the
              referenced statute prevails over any inconsistent interpretation here.
            </p>
          </Section>

          {/* §2 */}
          <Section id="information-we-collect" title="2. Information We Collect">
            <p>The data OneStamp collects and stores includes:</p>
            <Bullets items={[
              'Email address — used for sign-in, transactional notifications, and account recovery.',
              'Full name — used to personalise your in-app experience and to identify you to the merchants whose loyalty programs you join.',
              'Phone number — optional; used for contact and (where enabled) OTP verification.',
              'Date of birth — optional; used to grant birthday rewards.',
              'OAuth subject ID — when you sign in via a third-party identity provider, we store the provider-issued subject identifier in lieu of a password.',
              'Password — when you choose an email + password sign-in, your password is hashed with bcrypt before storage. The plaintext password is never persisted or transmitted in clear after the moment of authentication.',
              'Device identifiers — on the OneStamp mobile app, we record minimal device identifiers for session management.',
              'IP address — logged for security, fraud prevention, and abuse detection.',
              'Approximate location — when you enable the nearby-brands feature, your approximate location is used to surface relevant merchants. Precise GPS is not collected.',
            ]} />
          </Section>

          {/* §3 */}
          <Section id="how-we-use-information" title="3. How We Use Information">
            <Bullets items={[
              'Authentication and session management.',
              'Operating your loyalty cards, stamp balances, and reward eligibility.',
              'Sending transactional notifications (e.g., stamps issued, reward unlocked) and — only with your consent — optional marketing notifications.',
              'Fraud prevention, abuse detection, and platform safety.',
              'Service improvement through aggregated, non-identifying usage analytics.',
              'Compliance with applicable Indonesian legal and regulatory obligations, including tax retention (UU KUP Art. 28(11)) and any applicable KYC obligations.',
            ]} />
          </Section>

          {/* §4 */}
          <Section id="loyalty-program-data" title="4. Loyalty Program Data">
            <Bullets items={[
              'Per-brand stamp counts — how many stamps you have earned with each brand whose loyalty program you have joined.',
              'Multi-branch aggregation — when a brand operates multiple branches, your stamps are consolidated on a single card across all branches of that brand.',
              'Transaction records — invoice number, transaction amount, branch, timestamp. Indonesian tax law (UU KUP Art. 28(11)) requires us to retain transaction records for a minimum of 10 years.',
              'Visit history — the time and place of stamp issuance, used to detect anomalies and to power "recent activity" feeds.',
              'Owner-side aggregation — brand owners may view per-brand analytics in aggregated form. These aggregates are never sold to third parties or shared with other brands.',
            ]} />
          </Section>

          {/* §5 */}
          <Section id="rewards-and-redemption-data" title="5. Rewards & Redemption Data">
            <Bullets items={[
              'Reward redemption history — which reward was claimed, when, and at which branch.',
              'Stamp deduction records — including carry-over balances after a redemption (preserved per platform design).',
              'Reward eligibility state — locked or available, per brand.',
              'Promotional reward grants — including birthday rewards and referral bonuses, with the source promotion attached for audit.',
            ]} />
          </Section>

          {/* §6 */}
          <Section id="cookies-and-analytics" title="6. Cookies & Analytics">
            <Bullets items={[
              'Authentication cookies and tokens — required for sign-in. Without these, OneStamp cannot remember you between requests.',
              'No third-party advertising cookies. We do not run ad networks on OneStamp.',
              'Aggregated, non-identifying product analytics, used to improve the service.',
              'Analytics provider: PostHog (privacy-respecting product analytics). Data sent to PostHog is keyed to randomised identifiers, not to direct PII.',
            ]} />
          </Section>

          {/* §7 */}
          <Section id="data-sharing" title="7. Data Sharing">
            <p>
              <strong>We do not sell personal data.</strong> Data sharing is limited
              to the following narrowly-scoped categories:
            </p>
            <Bullets items={[
              'Merchants (brands) whose loyalty programs you join — limited to that specific brand\'s loyalty and transaction records. Brand X cannot see Brand Y\'s customers; strict per-tenant isolation is enforced server-side.',
              'Cloud hosting and platform sub-processors (Emergent platform infrastructure) operating under data processing agreements.',
              'Object storage provider (Tigris / S3-compatible) — used to store brand logos, banners, and user-uploaded images. Encrypted at rest.',
              'Email and SMS providers, when these channels are configured.',
              'Law enforcement and regulators — only in response to valid legal process and only to the extent strictly required.',
            ]} />
          </Section>

          {/* §8 */}
          <Section id="data-retention" title="8. Data Retention">
            <Bullets items={[
              'Active account — personal data is retained while your account remains active.',
              'Deleted account — see Section 9. After the 30-day grace period, personally identifying information is permanently anonymised.',
              'Transaction records — retained in anonymised form for at least 10 years to satisfy Indonesian tax retention (UU KUP Art. 28(11)).',
              'Audit logs — retained in anonymised form for fraud prevention and security audit purposes.',
              'Backups — a 7-day rolling backup window applies; backups taken before anonymisation may temporarily contain pre-anonymisation records and are purged from the rolling window naturally as new backups replace older ones.',
            ]} />
          </Section>

          {/* §9 — MANDATED LANGUAGE */}
          <Section id="account-deletion" title="9. Account Deletion">
            <p>
              <strong>Users can delete their account from within the app at any time.</strong>{' '}
              The deletion path is:
            </p>
            <p className="font-mono text-sm bg-white/[0.04] border border-white/8 rounded-lg px-3 py-2 text-zinc-200">
              Profile → Settings → Account → Delete Account
            </p>
            <Bullets items={[
              'Deletion requests enter a 30-day grace period during which the user may cancel the deletion by signing back in. A banner on the Home tab during this period offers a single-tap cancellation.',
              'After the grace period, personally identifiable information is permanently anonymized. The fields that are anonymized include email, name, phone, date of birth, and postal address; once anonymized these fields cannot be recovered.',
              'Transaction and redemption records required for legal, tax, fraud-prevention, and accounting purposes may be retained in anonymized form. These records cannot be linked back to a natural person after anonymization.',
              'Apple App Review Guideline 5.1.1(v) compliance: the in-app deletion option is reachable in 3 taps from the Profile tab.',
            ]} />
          </Section>

          {/* §10 */}
          <Section id="security" title="10. Security">
            <Bullets items={[
              'Encryption in transit — all traffic to and from OneStamp servers is protected by TLS / HTTPS.',
              'Encryption at rest — the database and object storage both apply at-rest encryption.',
              'Password hashing — passwords are stored as bcrypt hashes; plaintext passwords are never persisted.',
              'Periodic security audits and automated dependency vulnerability scans.',
              'Role-based access control — Customer, Cashier, Brand Owner, and Super Admin roles each receive a strictly-scoped permission set. Cross-tenant data access is blocked at the application layer.',
              'Documented incident-response and disaster-recovery procedures. We perform regular backups and have an exercised recovery runbook.',
            ]} />
          </Section>

          {/* §11 */}
          <Section id="user-rights" title="11. Your Rights">
            <p>Under Indonesian UU PDP and general data-protection best practice you have the following rights:</p>
            <Bullets items={[
              'Right to access — view the data we hold about you in-app on the Profile screen.',
              'Right to correct — update inaccurate data via the Profile edit screen.',
              'Right to delete — see Section 9.',
              'Right to data portability — for data portability requests, please contact support@onestamp.id.',
              'Right to object — you may opt out of optional marketing notifications in app settings while continuing to receive transactional notifications.',
              'Right to lodge a complaint — you may complain to the relevant Indonesian data-protection authority.',
            ]} />
          </Section>

          {/* §12 */}
          <Section id="contact" title="12. Contact Information">
            <p>For any privacy-related question, request, or concern:</p>
            <ul className="mt-3 space-y-2 text-zinc-300">
              <li>
                <span className="text-zinc-500">Contact email:</span>{' '}
                <a href="mailto:support@onestamp.id" className="text-[#C9A84C] hover:underline">
                  support@onestamp.id
                </a>
              </li>
            </ul>
          </Section>

          {/* Closing */}
          <section className="mt-12 pt-6 border-t border-white/10 text-sm text-zinc-500 space-y-2">
            <p><strong>Last updated:</strong> {LAST_UPDATED}.</p>
            <p>
              We may update this Privacy Policy from time to time. Material changes will be
              notified in-app before they take effect.
            </p>
          </section>

          {/* Back to top */}
          <div className="mt-10 text-center print:hidden">
            <a
              href="#top"
              onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
              data-testid="privacy-back-to-top"
              className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.16em] text-zinc-500 hover:text-[#C9A84C] transition"
            >
              <ArrowUp size={14} /> Back to top
            </a>
          </div>
        </article>
      </div>
    </div>
  );
}

// -- Helpers ---------------------------------------------------------

function Section({ id, title, children }) {
  return (
    <section className="mt-10 scroll-mt-24" id={id}>
      <h2 className="text-xl sm:text-2xl font-semibold text-white tracking-tight">
        {title}
      </h2>
      <div className="mt-3 space-y-3 text-zinc-300 leading-relaxed">
        {children}
      </div>
    </section>
  );
}

function Bullets({ items }) {
  return (
    <ul className="list-disc pl-5 space-y-2 marker:text-[#C9A84C]/60">
      {items.map((t, i) => <li key={i}>{t}</li>)}
    </ul>
  );
}
